<script setup lang="ts">
import { computed, onBeforeMount, onMounted, ref, watch } from 'vue';
import Calendar from 'primevue/calendar';
import { RouterLink, useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useOrganizationStore, useCalendarStore } from '@/store';
import { useRouting, useDateTime } from '@/composables';

const dates = ref();
const organizationStore = useOrganizationStore();
const $routing = useRouting();
const { organizationLogs } = storeToRefs(organizationStore);
const today = new Date();
const previousDatesApplied = ref<Date[]>();
const calendar = ref();
const dateWasChanged = ref(false);
const emit = defineEmits(['date-select']);
const calendarStore = useCalendarStore();
const route = useRouter();
const dateUtilities = useDateTime();

interface ItemPropsInterface {
  description: string;
  accessTenant?: {
    full_name_user: string;
    id: number;
    tenant_id: number;
    tenant_name: string;
  };
  subject: {
    id: string;
    name: string;
    display_name?: string;
    person?: {
      id: string;
      display_name: string;
    };
    documentable: { id: number; full_name: string };
    therapy_event_type?: string;
    end_at: string;
    start_at: string;
  };
  properties: {
    tab: string;
  };
}

onBeforeMount(async () => {
  await defineFilters();
  await getLogs();
});

onMounted(() => {
  const month = today.getMonth();
  const year = today.getFullYear();
  const start = new Date(year, month, today.getDate() - 7);
  const end = new Date(year, month, today.getDate());
  const defaultDates = [start, end];
  previousDatesApplied.value = defaultDates;
  dates.value = defaultDates;
});

const getLogs = async () => {
  await organizationStore.getOrganizationLogs(Number($routing.route.params.id));
};

const onPageChange = (event) => {
  organizationLogs.value.filters.per_page = event.rows;
  $routing.addQueryParams({ per_page: organizationLogs.value.filters.per_page, page: event.page + 1 });
  defineFilters();
};

const defineFilters = async () => {
  const query = $routing.route.query;

  organizationLogs.value.filters.search = (query.search ? query.search : '') as string;
  organizationLogs.value.filters.page = query.page ? Number(query.page) : 1;
  organizationLogs.value.filters.per_page = query.per_page ? Number(query.per_page) : 15;
};

const onSearch = () => {
  if (organizationLogs.value.filters.search && organizationLogs.value.filters.search.length >= 3) {
    return $routing.addQueryParams({ search: organizationLogs.value.filters.search });
  }
};

const closeCalendar = () => {
  calendar.value.overlayVisible = false;
  if (dateWasChanged.value) {
    previousDatesApplied.value = dates.value;
    dateWasChanged.value = false;
  }
  dates.value = previousDatesApplied.value;
};

const applyDates = () => {
  organizationLogs.value.filters.start_at = useDateTime().formatDateForApi(dates.value[0]);
  organizationLogs.value.filters.end_at = useDateTime().formatDateForApi(dates.value[1]);
  emit('date-select', dates.value);
  dateWasChanged.value = true;
  closeCalendar();
};

const showChanges = (new_values, old_values) => !Array.isArray(new_values) && !Array.isArray(old_values);

const itemSubjectInformation = (item: ItemPropsInterface) => {
  if (item.description == 'logged in.') {
    return '';
  }

  if (item.subject.person) {
    return item.subject.person.display_name;
  }

  if (item.subject.therapy_event_type) {
    return `${item.subject.therapy_event_type} ${dateUtilities.formatHourMinute(
      item.subject.start_at,
      true,
    )} - ${dateUtilities.formatHourMinute(item.subject.end_at, true)}`;
  }

  if (item.subject.documentable) return item.subject.documentable.full_name;
  else return item.subject.name ?? item.subject.display_name;
};

const redirectToEventCalendar = async (log: ItemPropsInterface['subject']) => {
  if (log.therapy_event_type) {
    calendarStore.redirectedToDate = useDateTime().fromUTCStringToLocalDate(log.start_at as string);
    return route.replace(`/calendar/day`);
  }
};

const redirectTo = (item: ItemPropsInterface) => {
  if (item.properties.tab) {
    if (item.properties.tab === 'Profile' && item.subject.name) return `/organizations/${item.subject.id}/profile`;

    if (item.properties.tab === 'Profile' && (item.subject.person?.display_name || item.subject.display_name))
      return `/user/${item.subject.id}`;

    if (item.properties.tab === 'Connections' && item.subject.name) return `/organizations/${item.subject.id}`;

    if (item.properties.tab === 'Connections' && item.subject.person?.display_name)
      return `/user/${item.subject.id}/connections`;

    if (item.properties.tab === 'Documents') return `/user/${item.subject.id}/documents`;

    if (item.properties.tab === 'Treatment Plan') return `/user/${item.subject.id}/treatment-plans`;
  }
  return '';
};

const isActivityLog = computed<boolean>(() => $routing.route.name === 'organization.logs');

watch(
  () => $routing.route.query,
  async () => {
    if (isActivityLog.value) {
      await defineFilters();

      await getLogs();
    }
  },
);

watch(
  () => organizationLogs.value.filters.start_at && organizationLogs.value.filters.end_at,
  async () => {
    if (isActivityLog.value) {
      await getLogs();
    }
  },
);

watch(
  () => organizationLogs.value.filters.search,
  async () => {
    if (organizationLogs.value.filters.search === '') {
      $routing.removeQueryParam('search');
    }
  },
);
</script>

<template>
  <Card>
    <template #header>
      <div class="flex w-full">
        <div class="mr-3">
          <IconField icon-position="left">
            <InputIcon>
              <Icon name="search" class="text-lg" />
            </InputIcon>
            <InputText
              v-model="organizationStore.organizationLogs.filters.search"
              type="text"
              placeholder="Search logs"
              @keydown.enter.prevent="onSearch"
            />
          </IconField>
        </div>
        <div class="w-full">
          <IconField icon-position="left">
            <InputIcon class="z-1">
              <Icon name="calendar_today" class="text-lg" />
            </InputIcon>
            <Calendar
              ref="calendar"
              v-model="dates"
              selection-mode="range"
              :max-date="today"
              :hide-on-date-time-select="true"
              class="daterangepicker-container w-17rem"
            >
              <template #footer>
                <div class="flex justify-content-end">
                  <AppButton type="secondary" text="Cancel" class="mr-2" @click="closeCalendar" />
                  <AppButton type="primary" text="Apply" :disabled="!dates[1]" @click="applyDates" />
                </div>
              </template>
            </Calendar>
          </IconField>
        </div>
      </div>
    </template>
    <template #content>
      <DataTable
        v-model:filters="organizationLogs.filters"
        :value="organizationLogs.data"
        :paginator="true"
        :rows="organizationLogs.filters.per_page"
        :total-records="organizationLogs.total"
        data-key="id"
        :row-hover="true"
        :lazy="true"
        loading-icon="logo-spinner"
        :loading="organizationLogs.loading"
        :rows-per-page-options="[15, 25, 30]"
        paginator-template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        current-page-report-template="Showing {first} to {last} of {totalRecords}"
        responsive-layout="scroll"
        @page="onPageChange"
      >
        <Column field="created_at" header="Datetime"></Column>
        <Column field="description" header="Event">
          <template #body="{ data }">
            <div class="flex flex-column py-1">
              <div class="pb-1">
                <div v-if="data.access_tenant" class="mr-1 inline-block">
                  {{ data.access_tenant.full_name_user }}
                </div>
                <div v-if="data.impersonated_by" class="mr-1 inline-block">
                  <RouterLink
                    v-if="!data.access_tenant"
                    :to="`/user/${data.impersonated_by.id}`"
                    class="font-semibold"
                    v-text="data.impersonated_by.display_name"
                  />
                  ( as
                  <RouterLink v-if="data.impersonated_by" :to="`/user/${data.causer.id}`" class="font-semibold">
                    {{ data.causer.display_name }}
                  </RouterLink>
                  )
                </div>
                <RouterLink
                  v-if="!data.impersonated_by && !data.access_tenant"
                  :to="`/user/${data.causer.id}`"
                  class="font-bold"
                  v-text="data.causer.display_name"
                />
                <span v-if="data.properties.student" class="mx-2">
                  {{ data.description.split(data.properties.student.name).shift() }}
                  <RouterLink
                    class="font-bold"
                    :to="`/user/${data.properties.student.id}`"
                    v-text="data.properties.student.name"
                  />
                  {{ data.description.split(data.properties.student.name).pop() }}
                </span>
                <span v-if="data.properties.person" class="mx-2">
                  {{ data.description.split(data.properties.person.name).shift() }}
                  <RouterLink
                    class="font-bold"
                    :to="`/user/${data.properties.person.id}`"
                    v-text="data.properties.person.name"
                  />
                  {{ data.description.split(data.properties.person.name).pop() }}
                </span>
                <span v-if="!data.properties.person && !data.properties.student" class="mx-2">
                  {{ data.description }}
                </span>
                <!-- start_path -->
                <RouterLink
                  v-if="data.subject.name"
                  :to="`/organizations/${data.subject.id}/profile`"
                  class="font-semibold"
                  @click="organizationStore.getOrganization(data.subject.id)"
                  v-text="itemSubjectInformation(data)"
                />
                <RouterLink
                  v-if="data.subject.person"
                  :to="`/user/${data.subject.person.id}`"
                  class="font-semibold"
                  v-text="itemSubjectInformation(data)"
                />
                <a
                  v-if="data.subject.therapy_event_type"
                  class="font-semibold"
                  @click="redirectToEventCalendar(data.subject)"
                  v-text="itemSubjectInformation(data)"
                />

                <RouterLink
                  v-if="data.subject.display_name"
                  :to="`/user/${data.subject.id}`"
                  class="font-semibold"
                  v-text="itemSubjectInformation(data)"
                />
                <span v-if="data.properties?.tab" class="mx-2">></span>
                <RouterLink :to="redirectTo(data)" class="font-semibold" v-text="data.properties.tab" />
                <span v-if="data.properties?.path" class="mx-2">></span>
                <span>{{ data.properties.path }}</span>
                <!-- end_path -->
              </div>
              <div
                v-if="showChanges(data.properties.old_values, data.properties.new_values)"
                class="flex flex-column w-12"
              >
                <div
                  v-for="(old, index) in data.properties.old_values"
                  :key="index"
                  class="text-sm flex align-items-center text-400"
                >
                  <span class="text-900 mr-1">{{ index.toString().replaceAll('_', ' ') }}:</span>
                  <span>{{ old }}</span>
                  <Icon v-if="old" class="mx-1" name="arrow_right_alt" />
                  <span class="text-900">{{ data.properties.new_values[index] }}</span>
                </div>
              </div>
            </div>
          </template>
        </Column>
        <template #empty>No Logs to View</template>
        <template #loading>Loading...</template>
      </DataTable>
    </template>
  </Card>
</template>

<style scoped lang="scss">
:deep(.p-card-body) {
  .p-datatable-header {
    background: $white;
    padding: 0.5rem;
  }

  .p-datatable-wrapper {
    margin: 0 !important;
  }

  .p-paginator-current {
    flex: 1;
    justify-content: flex-end;
  }
}
.daterangepicker-container {
  max-width: none !important;
  :deep(.p-inputtext) {
    font-weight: bold;
    padding-left: 30px;
  }
}
.rounded {
  border-radius: 8px;
}
</style>
